import { useState } from "react";
import {
  Monitor, Clock, Download, CheckCircle, AlertTriangle,
  ChevronDown, ChevronRight, Wifi, Shield, Zap, FileText,
  Camera, Play
} from "lucide-react";

const PHASES = [
  {
    id: "before",
    title: "BEFORE You Start (Do This Today)",
    color: "#4a9eff",
    icon: Download,
    steps: [
      {
        action: "Install Wireshark on your laptop",
        detail: "Download from wireshark.org (free). This captures EVERY packet between Toolbox and the MCU.",
        critical: true
      },
      {
        action: "Install Toolbox Proxy from Tesla Service",
        detail: "Log in to service.tesla.com, subscribe/pay for 1 hour, download and install Toolbox Proxy on your laptop.",
        critical: true
      },
      {
        action: "Make sure Fakra cable is connected to the car",
        detail: "LAN port left of steering wheel. Laptop Ethernet IP: 192.168.90.101 / Subnet: 255.255.255.0",
        critical: true
      },
      {
        action: "Verify you can ping the MCU",
        detail: "Open CMD, type: ping 192.168.90.100 - you should get replies.",
        critical: true
      },
      {
        action: "Enter Service Mode on the touchscreen",
        detail: "Tap Software, press and hold '85' for 10 seconds. Screen text turns RED = Service Mode active.",
        critical: true
      },
      {
        action: "Charge your laptop to 100%",
        detail: "You don't want it dying mid-session. Plug in if possible.",
        critical: false
      },
      {
        action: "Have a notepad ready (phone notes app works)",
        detail: "Write down anything interesting you see on screen - error codes, menu options, anything.",
        critical: false
      },
    ]
  },
  {
    id: "capture",
    title: "START Capture (Minute 0 - Do This FIRST)",
    color: "#e84057",
    icon: Camera,
    steps: [
      {
        action: "Open Wireshark",
        detail: "Launch it BEFORE you open Toolbox Proxy.",
        critical: true
      },
      {
        action: "Select your Ethernet adapter",
        detail: "It will be the one showing traffic. Usually called 'Ethernet' or 'Local Area Connection'. NOT Wi-Fi.",
        critical: true
      },
      {
        action: "Set capture filter (optional but helpful)",
        detail: "In the filter bar at top, type: host 192.168.90.100 and press Enter. This only captures MCU traffic.",
        critical: false
      },
      {
        action: "Click the blue shark fin (Start Capture)",
        detail: "Wireshark is now recording every packet. Leave it running the ENTIRE hour.",
        critical: true
      },
      {
        action: "NOW launch Toolbox Proxy",
        detail: "Open Toolbox Proxy. It should detect the car and show Service Mode Plus on the MCU screen.",
        critical: true
      },
      {
        action: "Take a screenshot of the Toolbox Proxy main screen",
        detail: "We want to see what menus and options are available.",
        critical: false
      },
    ]
  },
  {
    id: "priority",
    title: "PRIORITY Tasks (Minutes 1-30)",
    color: "#2dd4a8",
    icon: Zap,
    steps: [
      {
        action: "CHARGE PORT DOOR - Find and run the reset/reprogram routine",
        detail: "Look for: Charging > Charge Port > Reset/Relearn/Calibrate. Run it. Note the exact menu path.",
        critical: true
      },
      {
        action: "PYROTECHNIC FUSE - Check status and reset if possible",
        detail: "Look for: HV Battery > Safety > Pyrotechnic Fuse / MSD. Check if it shows status (green/red). Run any reset available.",
        critical: true
      },
      {
        action: "DTC SCAN - Run a full vehicle diagnostic scan",
        detail: "Look for: Diagnostics > Scan All / Full Scan. Let it complete. Screenshot EVERY error code.",
        critical: true
      },
      {
        action: "CLEAR DTCs if possible after scanning",
        detail: "After scanning, try to clear codes. Some may not clear (that tells us something too).",
        critical: false
      },
      {
        action: "Screenshot every screen you visit",
        detail: "Windows: Win+Shift+S for snip tool. Screenshot menus, options, results, errors - EVERYTHING.",
        critical: true
      },
    ]
  },
  {
    id: "explore",
    title: "EXPLORE Everything (Minutes 30-50)",
    color: "#f5a623",
    icon: Monitor,
    steps: [
      {
        action: "Browse EVERY menu in Toolbox",
        detail: "Click through every category: Battery, Drive Unit, Suspension, HVAC, Gateway, Steering, Charging, Body. Screenshot each.",
        critical: true
      },
      {
        action: "Check Firmware Versions",
        detail: "Look for: Vehicle Info or Firmware or Software versions. Note every module and version number.",
        critical: false
      },
      {
        action: "Run any read/diagnostic routine you see",
        detail: "Anything that says 'Read', 'Check', 'Test', 'Status' - run it. These are safe. Wireshark captures the data.",
        critical: true
      },
      {
        action: "Check Gateway status",
        detail: "Look for Gateway > Unlock or Security. See if it shows locked/unlocked status.",
        critical: false
      },
      {
        action: "Look at Live Data if available",
        detail: "Some Toolbox versions show real-time CAN data. If you see it, let it run for 30 seconds.",
        critical: false
      },
      {
        action: "Check for any Suspension routines",
        detail: "Air Suspension > Calibrate / Jack Mode / Ride Height. Run reads only.",
        critical: false
      },
    ]
  },
  {
    id: "finish",
    title: "SAVE & CLOSE (Minutes 50-60)",
    color: "#a78bfa",
    icon: FileText,
    steps: [
      {
        action: "Run one more full DTC scan",
        detail: "Final scan to capture any new codes from the routines we ran.",
        critical: false
      },
      {
        action: "Close Toolbox Proxy",
        detail: "Exit the program normally. Wireshark captures the disconnect sequence too.",
        critical: true
      },
      {
        action: "STOP Wireshark capture",
        detail: "Click the red square (Stop). Then File > Save As > pick a location > save as .pcapng file.",
        critical: true
      },
      {
        action: "Name the file: toolbox_capture_YYYY-MM-DD.pcapng",
        detail: "Save it somewhere safe. This file is GOLD - it contains every command Toolbox sent to the MCU.",
        critical: true
      },
      {
        action: "Upload the .pcapng file to me",
        detail: "Also upload all your screenshots. I will reverse-engineer every endpoint, payload, and auth flow from the capture.",
        critical: true
      },
      {
        action: "Exit Service Mode on the touchscreen",
        detail: "If needed, same process: Software > hold 85 > toggle off. Or just let it time out.",
        critical: false
      },
    ]
  },
];

export default function ServiceModeGuide() {
  const [expandedPhase, setExpandedPhase] = useState("before");
  const [checkedSteps, setCheckedSteps] = useState({});

  const toggleCheck = (phaseId, stepIdx) => {
    const key = phaseId + "-" + stepIdx;
    setCheckedSteps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const totalSteps = PHASES.reduce((sum, p) => sum + p.steps.length, 0);
  const checkedCount = Object.values(checkedSteps).filter(Boolean).length;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1200, margin: "0 auto" }}>
      <style>{`
        .gcard { background:#0e0e16; border:1px solid #1a1a28; border-radius:10px; padding:20px; margin-bottom:12px; }
        .gtitle { font-size:11px; text-transform:uppercase; letter-spacing:2px; color:#6b7280; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
        .phase-header { display:flex; align-items:center; gap:12px; padding:14px 16px; cursor:pointer; border-radius:10px; border:1px solid #1a1a28; background:#0e0e16; transition:all .15s; margin-bottom:8px; }
        .phase-header:hover { border-color:#2a2a3e; }
        .step-row { display:flex; align-items:flex-start; gap:12px; padding:10px 14px 10px 42px; border-radius:8px; transition:background .1s; }
        .step-row:hover { background:rgba(255,255,255,0.02); }
        .step-check { width:20px; height:20px; border-radius:4px; border:2px solid #2a2a3e; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:all .15s; margin-top:1px; }
        .step-check.checked { background:#2dd4a8; border-color:#2dd4a8; }
        .step-check:hover { border-color:#4a9eff; }
        .progress-bar { width:100%; height:6px; background:#1a1a2a; border-radius:3px; overflow:hidden; }
        .progress-fill { height:100%; border-radius:3px; transition:width .3s; background:linear-gradient(90deg, #2dd4a8, #4a9eff); }
      `}</style>

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0", letterSpacing: 1, marginBottom: 4 }}>Service Mode Setup Guide</h1>
        <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
          Step-by-step instructions for entering Service Mode and capturing Toolbox data
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="progress-bar" style={{ flex: 1 }}>
            <div className="progress-fill" style={{ width: `${(checkedCount / totalSteps) * 100}%` }} />
          </div>
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{checkedCount}/{totalSteps}</span>
        </div>
      </div>

      {/* Service Mode Instructions */}
      <div className="gcard" style={{ borderColor: "rgba(232,64,87,0.25)", marginBottom: 20 }}>
        <div className="gtitle"><AlertTriangle size={14} color="#e84057" /> How to Enter Service Mode (Owner - Free)</div>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.8 }}>
              <strong style={{ color: "#f5a623" }}>1.</strong> On the touchscreen, tap <strong>Software</strong><br/>
              <strong style={{ color: "#f5a623" }}>2.</strong> Press and hold <strong style={{ color: "#e84057" }}>85</strong> for <strong>10 seconds</strong><br/>
              <strong style={{ color: "#f5a623" }}>3.</strong> Screen text turns <strong style={{ color: "#e84057" }}>RED</strong> = Service Mode active
            </div>
            <p style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>
              This is a physical touchscreen action only. No software can do this remotely.
              Service Mode PLUS (port 4070 / ODIN) requires Toolbox Proxy subscription from Tesla.
            </p>
          </div>
          <div style={{ width: 120, height: 80, borderRadius: 8, background: "rgba(232,64,87,0.1)", border: "1px solid rgba(232,64,87,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
            <span style={{ fontSize: 24, fontWeight: 900, color: "#e84057" }}>85</span>
            <span style={{ fontSize: 9, color: "#e84057", letterSpacing: 1 }}>HOLD 10 SEC</span>
          </div>
        </div>
      </div>

      {/* Toolbox Capture Checklist */}
      {PHASES.map(phase => {
        const Icon = phase.icon;
        const isExpanded = expandedPhase === phase.id;
        const phaseChecked = phase.steps.filter((_, i) => checkedSteps[phase.id + "-" + i]).length;

        return (
          <div key={phase.id}>
            <div className="phase-header" onClick={() => setExpandedPhase(isExpanded ? null : phase.id)}
              style={{ borderColor: isExpanded ? phase.color + "40" : "#1a1a28" }}
              data-testid={`phase-${phase.id}`}>
              {isExpanded ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
              <div style={{ width: 36, height: 36, borderRadius: 8, background: phase.color + "15", border: `1px solid ${phase.color}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size={18} color={phase.color} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{phase.title}</div>
              </div>
              <span style={{ fontSize: 11, color: phaseChecked === phase.steps.length ? "#2dd4a8" : "#6b7280" }}>
                {phaseChecked}/{phase.steps.length}
              </span>
            </div>

            {isExpanded && phase.steps.map((step, i) => {
              const key = phase.id + "-" + i;
              const isChecked = checkedSteps[key];
              return (
                <div key={i} className="step-row">
                  <div className={`step-check ${isChecked ? "checked" : ""}`}
                    onClick={() => toggleCheck(phase.id, i)}
                    data-testid={`check-${phase.id}-${i}`}>
                    {isChecked && <CheckCircle size={14} color="#0e0e16" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: isChecked ? "#6b7280" : "#e2e8f0", fontWeight: 600, textDecoration: isChecked ? "line-through" : "none", display: "flex", alignItems: "center", gap: 6 }}>
                      {step.action}
                      {step.critical && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "rgba(232,64,87,0.15)", color: "#e84057", fontWeight: 700, letterSpacing: 1 }}>MUST</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{step.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
